'use strict';

function number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getTotalOutput(summary) {
  return number(summary && summary.actualOutput) + number(summary && summary.qualityLossQty);
}

// Running machines first. Callers pass a limit only when the surface cannot
// scroll; the Floor Deck panel scrolls, so it takes the full list.
function getVisibleMachineCards(machines, isRunning, limit = Infinity) {
  const sorted = [...(machines || [])]
    .sort((left, right) => Number(Boolean(isRunning(right))) - Number(Boolean(isRunning(left))));
  return Number.isFinite(limit) ? sorted.slice(0, limit) : sorted;
}

function aggregateDailyOee(rows, daysInMonth) {
  const totals = new Array(daysInMonth).fill(0);
  const counts = new Array(daysInMonth).fill(0);

  (rows || []).forEach((row) => {
    const day = Number(String(row.date || '').slice(-2));
    const value = number(row.avg);
    if (day >= 1 && day <= daysInMonth && value > 0) {
      totals[day - 1] += value;
      counts[day - 1] += 1;
    }
  });

  return totals.map((total, index) => counts[index] > 0
    ? Number((total / counts[index]).toFixed(1))
    : 0);
}

// Compares the selected day's OEE against the previous day of the same daily
// series. Trends are fetched one month at a time, so the 1st of a month has no
// comparison point, and days with no recorded OEE aggregate to 0.
function getOeeVsYesterday(dailyOee, date, currentOee) {
  const rows = dailyOee || [];
  const dayIndex = Number(String(date || '').slice(8, 10)) - 1;
  const previous = dayIndex >= 1 ? number(rows[dayIndex - 1]) : 0;
  if (previous <= 0) return { available: false, delta: 0, direction: 'flat' };

  const delta = Number((number(currentOee) - previous).toFixed(1));
  return {
    available: true,
    delta,
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
  };
}

function isToday(date) {
  const now = new Date();
  const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return date === localDate;
}

function getFloorDeckDateLabel(selectedDate, now = new Date()) {
  const selectedTimestamp = getDateTimestamp(selectedDate);
  const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const currentTimestamp = getDateTimestamp(currentDate);
  if (selectedTimestamp === null || currentTimestamp === null) return 'Date unavailable';

  const dayOffset = Math.round((selectedTimestamp - currentTimestamp) / 86400000);
  if (dayOffset === 0) return 'Today';
  if (dayOffset === -1) return '1 day ago';
  if (dayOffset < 0) return `${Math.abs(dayOffset)} days ago`;
  if (dayOffset === 1) return 'In 1 day';
  return `In ${dayOffset} days`;
}

function formatDayRange(date) {
  if (!date) return '';
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year} 00:00 — ${day}/${month}/${year} 23:59`;
}

function getCurrentMonthRange(date) {
  const [year, month] = date.split('-');
  const lastDay = new Date(Number(year), Number(month), 0).getDate();
  return {
    start: `${year}-${month}-01`,
    end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
    daysInMonth: lastDay,
  };
}

function getJobProgress(job) {
  const planned = number(job && job.plannedOutput);
  const actual = number(job && job.actualOutput);
  const reported = Number(job && job.progress);

  if (Number.isFinite(reported)) return reported;
  return planned > 0 ? Number(((actual / planned) * 100).toFixed(2)) : 0;
}

function getDateTimestamp(value) {
  const date = String(value || '').slice(0, 10);
  const timestamp = Date.parse(`${date}T00:00:00Z`);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function getFloorDeckJobOrderPresentation(job, selectedDate) {
  const planned = number(job && job.plannedOutput);
  const actual = number(job && job.actualOutput);
  const progress = getJobProgress(job);
  const dueTimestamp = getDateTimestamp(job && job.dueDate);
  const selectedTimestamp = getDateTimestamp(selectedDate);
  const dayOffset = dueTimestamp !== null && selectedTimestamp !== null
    ? Math.round((dueTimestamp - selectedTimestamp) / 86400000)
    : null;
  const complete = progress >= 100;

  let dueLabel = 'Due date unavailable';
  if (complete) dueLabel = 'Completed';
  else if (dayOffset === null) dueLabel = 'Due date unavailable';
  else if (dayOffset < 0) dueLabel = 'Overdue';
  else if (dayOffset === 0) dueLabel = 'Due today';
  else if (dayOffset === 1) dueLabel = 'Due tomorrow';
  else dueLabel = `Due in ${dayOffset} days`;

  let tone = 'on-track';
  if (progress > 100) tone = 'over-target';
  else if (complete) tone = 'complete';
  else if (dayOffset !== null && dayOffset < 0) tone = 'behind';

  return {
    progress,
    barProgress: Math.max(0, Math.min(100, progress)),
    variance: actual - planned,
    tone,
    dueLabel,
  };
}

function sortFloorDeckJobOrders(jobs, sortMode) {
  const rows = [...(jobs || [])];

  return rows.sort((left, right) => {
    if (sortMode === 'progress') {
      return getJobProgress(right) - getJobProgress(left)
        || String(left.jobNo || '').localeCompare(String(right.jobNo || ''));
    }

    if (sortMode === 'due-date') {
      const leftDue = getDateTimestamp(left.dueDate);
      const rightDue = getDateTimestamp(right.dueDate);
      return (leftDue === null ? Number.MAX_SAFE_INTEGER : leftDue) - (rightDue === null ? Number.MAX_SAFE_INTEGER : rightDue)
        || String(left.jobNo || '').localeCompare(String(right.jobNo || ''));
    }

    return (number(left.actualOutput) - number(left.plannedOutput)) - (number(right.actualOutput) - number(right.plannedOutput))
      || getJobProgress(left) - getJobProgress(right)
      || String(left.jobNo || '').localeCompare(String(right.jobNo || ''));
  });
}

module.exports = {
  aggregateDailyOee,
  formatDayRange,
  getFloorDeckDateLabel,
  getFloorDeckJobOrderPresentation,
  getCurrentMonthRange,
  getOeeVsYesterday,
  getTotalOutput,
  getVisibleMachineCards,
  isToday,
  sortFloorDeckJobOrders,
};
